
import { useState, useEffect, useCallback } from "react";
import { AgentListItem } from "@/types/agent";
import { getAgentById } from "@/services/agentService";
import { apiConfig } from "@/lib/env";

// Cache for agents to prevent redundant API calls
const agentCache = new Map<string, AgentListItem>();

interface UseAgentManagementProps {
  initialAgentId?: string;
}

export const useAgentManagement = ({ initialAgentId }: UseAgentManagementProps = {}) => {
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [selectedAgent, setSelectedAgent] = useState<AgentListItem | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [isManualSelection, setIsManualSelection] = useState(false);

  // Fetch default agent from API
  const fetchDefaultAgentId = async (): Promise<string> => {
    try {
      const response = await fetch(`${apiConfig.baseUrl}/agents/default`);
      if (!response.ok) {
        throw new Error(`Failed to fetch default agent: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🤖 Default agent API response:', data);
      
      return data.default_agent_id || "agent-001";
    } catch (error) {
      console.error("Failed to fetch default agent ID:", error);
      return "agent-001"; // Fallback
    }
  };

  // Initialize with default agent - using cache to prevent redundant calls
  useEffect(() => {
    let isMounted = true;
    
    const initAgent = async () => {
      try {
        setInitError(null);
        
        // Skip initialization if user has manually selected an agent
        if (isManualSelection) {
          return;
        }
        
        // Determine which agent ID to use
        let agentIdToUse = initialAgentId;
        
        if (!agentIdToUse) {
          // Fetch default agent ID from API
          agentIdToUse = await fetchDefaultAgentId();
          console.log('🎯 Using default agent ID from API:', agentIdToUse);
        }
        
        if (!selectedAgentId || selectedAgentId !== agentIdToUse) {
          // Check cache first
          const cachedAgent = agentCache.get(agentIdToUse);
          if (cachedAgent && isMounted) {
            setSelectedAgentId(agentIdToUse);
            setSelectedAgent(cachedAgent);
            setIsInitializing(false);
            return;
          }

          // Fetch agent details
          const agent = await getAgentById(agentIdToUse);
          if (agent && isMounted) {
            agentCache.set(agentIdToUse, agent);
            setSelectedAgentId(agentIdToUse);
            setSelectedAgent(agent);
            setIsInitializing(false);
          }
        } else if (!selectedAgent && isMounted) {
          // We have an agentId but no agent object, fetch it
          const cachedAgent = agentCache.get(selectedAgentId);
          if (cachedAgent) {
            setSelectedAgent(cachedAgent);
            setIsInitializing(false);
            return;
          }

          const agent = await getAgentById(selectedAgentId);
          if (agent) {
            agentCache.set(selectedAgentId, agent);
            setSelectedAgent(agent);
            setIsInitializing(false);
          }
        }
      } catch (error) {
        console.error("Failed to initialize agent:", error);
        if (isMounted) {
          setInitError(error instanceof Error ? error.message : "Failed to initialize agent");
          setIsInitializing(false);
        }
      }
    };
    
    initAgent();
    
    return () => {
      isMounted = false;
    };
  }, [initialAgentId, selectedAgentId, selectedAgent, isManualSelection]);

  const changeAgent = useCallback((agentId: string, agent: AgentListItem) => {
    if (agentId && agent) {
      // Cache the agent
      agentCache.set(agentId, agent);
      setSelectedAgentId(agentId);
      setSelectedAgent(agent);
      setIsManualSelection(true); // Mark as manual selection
    } else {
      console.error("Invalid agent data provided to changeAgent:", { agentId, agent });
    }
  }, []);

  // Log any initialization errors but don't throw them
  useEffect(() => {
    if (initError) {
      console.error("Agent initialization error:", initError);
    }
  }, [initError]);

  return {
    selectedAgentId,
    selectedAgent,
    isInitializing,
    initError,
    changeAgent
  };
};
